import { useState, useRef, useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { insiderTradesService } from '../services/insiderTradesService';
import type { InsiderTrade } from '../types/insiderTrades';
import { TZ } from '../utils/marketTime';

interface InsiderTradesWidgetProps {
  onRemove: () => void;
  ticker?: string;
}

const FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'P-Purchase', label: 'Purchases' },
  { value: 'S-Sale', label: 'Sales' },
] as const;

const formatDate = (dateStr: string): string => {
  if (!dateStr) return '-';
  // Handle both date-only ("2026-03-18") and datetime ("2026-03-18 21:50:19")
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
    ? dateStr + 'T00:00:00'
    : dateStr.replace(' ', 'T');
  const date = new Date(normalized);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', { timeZone: TZ, month: 'short', day: 'numeric', year: 'numeric' });
};

const formatNumber = (n: number | null): string => {
  if (n === null || n === undefined) return '-';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
};

const formatShares = (n: number | null): string => {
  if (n === null || n === undefined) return '-';
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

const getTransactionStyle = (type: string): { bg: string; text: string } => {
  if (type?.startsWith('P-')) return { bg: 'bg-bull-blue/20', text: 'text-bull-blue' };
  if (type?.startsWith('S-')) return { bg: 'bg-bear-red/20', text: 'text-bear-red' };
  return { bg: 'bg-accent-yellow/20', text: 'text-accent-yellow' };
};

export function InsiderTradesWidget({ onRemove, ticker }: InsiderTradesWidgetProps) {
  const [transactionType, setTransactionType] = useState('');
  const [search, setSearch] = useState(ticker?.toUpperCase() ?? '');
  const [searchInput, setSearchInput] = useState(ticker?.toUpperCase() ?? '');
  const sentinelRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['insiderTrades', transactionType, search],
      queryFn: ({ pageParam }) =>
        insiderTradesService.fetchTrades({
          page: pageParam as number,
          perPage: 20,
          transactionType: transactionType || undefined,
          search: search || undefined,
        }),
      getNextPageParam: (lastPage) =>
        lastPage.page < lastPage.total_pages ? lastPage.page + 1 : undefined,
      initialPageParam: 1,
      staleTime: 5 * 60 * 1000,
      refetchInterval: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    });

  const allTrades = data?.pages.flatMap((p) => p.results) ?? [];

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const handleFilterChange = (value: string) => {
    setTransactionType(value);
  };

  return (
    <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
      {/* Title Bar - drag handle */}
      <div className="widget-drag-handle flex items-center px-3 py-1.5 bg-chrome border-b border-dark-border cursor-move relative z-20">
        <h3 className="text-sm font-bold text-white uppercase tracking-[1.5px]">Insider Trades</h3>
        <button
          onClick={onRemove}
          className="text-gray-400 hover:text-white hover:bg-bear-red/20 text-sm px-2 py-0.5 rounded transition-colors"
          title="Close widget"
        >
          ×
        </button>
      </div>

      {/* Toolbar: filter toggle buttons */}
      <div className="flex items-center gap-1 px-3 py-1.5 bg-dark-bg/50 border-b border-dark-border relative z-20">
        {FILTER_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => handleFilterChange(opt.value)}
            className={`px-3 py-0.5 text-xs rounded-sm transition-colors ${
              transactionType === opt.value
                ? 'bg-dark-surface-light text-white border-b-2 border-accent-yellow'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="px-3 py-2 border-b border-dark-border relative z-20">
        <div className="flex gap-2">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by ticker or name..."
            className="flex-1 px-2 py-1 text-xs bg-dark-surface border border-dark-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-accent-yellow"
          />
          <button
            type="submit"
            className="px-3 py-1 text-xs bg-dark-surface-light hover:bg-dark-border text-white rounded transition-colors"
          >
            Search
          </button>
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                setSearchInput('');
              }}
              className="px-2 py-1 text-xs text-gray-400 hover:text-white transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      </form>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {isLoading && (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
            Loading...
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center h-full text-bear-red text-sm">
            Error loading data
          </div>
        )}

        {allTrades.length > 0 && (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-dark-surface-light border-b border-dark-border">
              <tr>
                <th className="px-2 py-1.5 text-left font-semibold text-gray-300">Filing Date</th>
                <th className="px-2 py-1.5 text-left font-semibold text-gray-300">Ticker</th>
                <th className="px-2 py-1.5 text-left font-semibold text-gray-300">Name</th>
                <th className="px-2 py-1.5 text-left font-semibold text-gray-300">Type</th>
                <th className="px-2 py-1.5 text-left font-semibold text-gray-300">Txn Type</th>
                <th className="px-2 py-1.5 text-right font-semibold text-gray-300">Shares</th>
                <th className="px-2 py-1.5 text-right font-semibold text-gray-300">Price</th>
                <th className="px-2 py-1.5 text-right font-semibold text-gray-300">Total Value</th>
              </tr>
            </thead>
            <tbody>
              {allTrades.map((trade: InsiderTrade) => {
                const txStyle = getTransactionStyle(trade.transaction_type);
                return (
                  <tr
                    key={trade.id}
                    className="border-b border-dark-border hover:bg-dark-surface-light transition-colors"
                  >
                    <td
                      className="px-2 py-1.5 text-gray-400"
                      title={`Transaction: ${formatDate(trade.transaction_date)}`}
                    >
                      {formatDate(trade.filing_date)}
                    </td>
                    <td className="px-2 py-1.5">
                      <a
                        href={`/ticker/${trade.ticker}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-white hover:text-bull-blue transition-colors"
                      >
                        {trade.ticker}
                      </a>
                    </td>
                    <td
                      className="px-2 py-1.5 text-white truncate max-w-[120px]"
                      title={trade.reporting_name}
                    >
                      {trade.reporting_name}
                    </td>
                    <td className="px-2 py-1.5 text-gray-400 truncate max-w-[80px]" title={trade.type_of_owner}>
                      {trade.type_of_owner}
                    </td>
                    <td className="px-2 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-xs ${txStyle.bg} ${txStyle.text}`}>
                        {trade.transaction_type}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-right font-numeric text-white">
                      {formatShares(trade.securities_transacted)}
                    </td>
                    <td className="px-2 py-1.5 text-right font-numeric text-white">
                      {trade.price !== null && trade.price !== undefined
                        ? `$${trade.price.toFixed(2)}`
                        : '-'}
                    </td>
                    <td className="px-2 py-1.5 text-right font-numeric text-white">
                      {formatNumber(trade.total_value)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {!isLoading && allTrades.length === 0 && (
          <div className="flex items-center justify-center h-full text-gray-500 text-sm">
            {search ? 'No results found' : 'No trades available'}
          </div>
        )}

        {/* Infinite scroll sentinel */}
        <div ref={sentinelRef} className="h-6 flex items-center justify-center">
          {isFetchingNextPage && (
            <span className="text-xs text-gray-600">Loading more...</span>
          )}
        </div>
      </div>
    </div>
  );
}
